import api from './api';
import { calculateIbGrade } from './utils';
import type { Assignment, Grade, GradeScale, Enrollment } from '@/types/api';

/**
 * One gradebook cell. `percentage` is rounded for display only; `pointsEarned`
 * is the exact stored value and is what every editor must prefill with —
 * reconstructing points from the rounded percentage corrupts any score whose
 * total is not a divisor of 100 (22/30 → 73% → 21.9).
 */
export interface GradebookCellGrade {
  gradeId: string;
  /**
   * Null when the result was measured on a scale that is not percentage (AR-24).
   * Not zero: `Math.round(null)` is 0, and a PYP 'DE' shown as 0% is a failing
   * grade the school never awarded.
   */
  percentage: number | null;
  pointsEarned?: number;
  /** The scale the result is measured on, when it is not percentage. */
  scaleKey?: string;
  /** The code on that scale — what the cell shows in place of a percentage. */
  scoreCode?: string;
  status: Grade['status'];
  feedback?: string;
}

export interface GradebookStudent {
  id: string;
  name: string;
  grades: Record<string, GradebookCellGrade>;
  currentGrade?: number;
  predictedIbGrade?: number;
}

/** Assignments that exist but contribute nothing, because they have no category. */
export interface UncategorizedAssignmentsWarning {
  count: number;
  names: string[];
  /** The server's sentence, singular or plural. Render it; do not compose one. */
  message: string;
}

/**
 * What the server says about whether this course's term grade means what a teacher
 * thinks it means (AR-11). Two independent states that compose:
 *
 *  - `ok: false` — a **refusal**. The category weights do not total 100%, so there
 *    is no term grade at all, and the stored one has been cleared rather than left
 *    standing. It may also carry the warning below.
 *  - `uncategorizedAssignments` — a **warning**. The grade is valid; it just
 *    excludes assignments that have no category.
 *
 * Null when there is nothing to say — the server omits the field entirely, so
 * presence means "there is something to show".
 *
 * The messages are written server-side so the wording a teacher reads cannot drift
 * from the rule that produced it. `totalWeight` is a fraction (0.9 = 90%) and is
 * carried for diagnosis only — the refusal message already states it as a percent.
 */
export type GradeCalculationNotice =
  | {
      ok: false;
      reason: 'category-weights-do-not-total-100';
      message: string;
      totalWeight: number;
      uncategorizedAssignments?: UncategorizedAssignmentsWarning;
    }
  | { ok: true; uncategorizedAssignments: UncategorizedAssignmentsWarning };

export interface GradebookRoster {
  students: GradebookStudent[];
  gradeCalculation: GradeCalculationNotice | null;
}

/**
 * The headline pair, from the enrollment that carries it.
 *
 * The term grade is the server's category-weighted number, recomputed on every
 * grade save and stored on the enrollment. Averaging the cell percentages here
 * would ignore categories and weights entirely.
 *
 * Written once because it is applied twice: when the grid is first built, and
 * again on the way out of the cache, where the enrollment in hand may be newer
 * than the one the cached grid was built from (`useGradebookStudents`).
 */
export function headlineGrade(currentGrade: number | null | undefined): {
  currentGrade?: number;
  predictedIbGrade?: number;
} {
  // Both keys always, never an empty object: this is spread over a row that may
  // already carry a stale pair, and an absent key would leave the old number
  // standing where the server has just cleared it (a weights refusal does
  // exactly that).
  if (currentGrade == null) return { currentGrade: undefined, predictedIbGrade: undefined };
  const rounded = Math.round(currentGrade);
  return { currentGrade: rounded, predictedIbGrade: calculateIbGrade(rounded) };
}

/**
 * The gradebook grid for one term-course.
 *
 * Built from the enrollments the caller already holds, and one read of this
 * course's grades — the whole grid in a single round trip.
 *
 * It used to take three, one after the other: the whole school's roster, then
 * the grades, then the same enrollments the caller had already fetched and was
 * passing in the query key. Two of the three were for data already in hand, and
 * being serial they sat on the critical path end to end — most of the wait
 * between clicking a course and seeing it.
 *
 * The roster read was also wrong. `/api/students` is paginated at 50 and this
 * asked for it unpaged, so in a school with more students than that, every child
 * past the first page was missing from the grid — no error, no empty row, just
 * absent. An enrollment is what puts a student in a class, so the enrollments
 * *are* the roster: it cannot truncate, and it cannot include someone who is not
 * in the course.
 *
 * Withdrawn and dropped rows are left out. `DELETE /api/enrollments/:id` is a
 * soft delete precisely so the marks survive, which means the row stays behind
 * after a student leaves the class; it must not stay in the grid.
 */
export async function getStudentsWithGrades(
  termCourseId: string,
  enrollments: Enrollment[]
): Promise<GradebookRoster> {
  const gradesResponse = await api.get<{
    grades: Grade[];
    // Teacher/admin only: the server withholds it from students and parents.
    gradeCalculation?: GradeCalculationNotice;
  }>(`/api/grades/term-course/${termCourseId}`);
  const allGrades = gradesResponse.data.grades;
  // The notice rides on the read the grid already makes, so surfacing it costs
  // no extra round-trip.
  const gradeCalculation = gradesResponse.data.gradeCalculation ?? null;

  // Grouped once rather than re-scanned per student: a class of 30 against a
  // term's worth of marks is 30 passes over the same array otherwise.
  const gradesByStudent = new Map<string, Grade[]>();
  allGrades.forEach((grade) => {
    const forStudent = gradesByStudent.get(grade.studentId);
    if (forStudent) forStudent.push(grade);
    else gradesByStudent.set(grade.studentId, [grade]);
  });

  const students: GradebookStudent[] = enrollments
    .filter((enrollment) => enrollment.status === 'active')
    .map((enrollment) => {
      const grades: Record<string, GradebookCellGrade> = {};
      (gradesByStudent.get(enrollment.studentId) ?? []).forEach((grade) => {
        grades[grade.assignmentId] = {
          gradeId: grade.id,
          percentage:
            grade.calculatedPercentage === null
              ? null
              : Math.round(grade.calculatedPercentage),
          pointsEarned: grade.pointsEarned,
          scaleKey: grade.scaleKey ?? undefined,
          scoreCode: grade.scoreCode ?? undefined,
          status: grade.status,
          // Normalised like `scaleKey` and `scoreCode` above: the column is
          // nullable, so a grade nobody has written on arrives as an explicit
          // null. Passing that through made every editor that preserves the
          // existing feedback while saving a score send `feedback: null` back,
          // and `PUT /api/grades/:id` rejects it — re-marking a student who had
          // no feedback failed validation.
          feedback: grade.feedback ?? undefined,
        };
      });

      return {
        id: enrollment.studentId,
        name: `${enrollment.studentFirstName ?? ''} ${enrollment.studentLastName ?? ''}`.trim(),
        grades,
        ...headlineGrade(enrollment.currentGrade),
      };
    });

  return { students, gradeCalculation };
}

// Get assignments
export async function getAssignments(termCourseId?: string) {
  try {
    let url = '/api/assignments';

    // If termCourseId is provided, get assignments for that term-course
    if (termCourseId) {
      url = `/api/assignments/term-course/${termCourseId}`;
    }

    const response = await api.get<{ assignments: Assignment[] }>(url);

    // Transform to match gradebook format. `categoryId` rides along because a
    // marking surface has to say what kind of work an assignment is; without it
    // that costs one round-trip per assignment to re-read what this call already
    // had in hand. `weight` deliberately does not: it is not a share of the term
    // grade (the categories carry those) and every assignment stores 1.0, so
    // there is nothing a reader could do with it but misread it.
    return response.data.assignments.map(assignment => ({
      id: assignment.id,
      name: assignment.assignmentName,
      totalPoints: assignment.totalPoints,
      categoryId: assignment.categoryId,
      dueDate: assignment.dueDate,
    }));
  } catch (error) {
    console.error('Error fetching assignments:', error);
    // Return empty array if no assignments found
    return [];
  }
}

// Get a specific grade
export async function getGrade(gradeId: string) {
  const response = await api.get<{ grade: Grade }>(`/api/grades/${gradeId}`);
  return response.data.grade;
}

// Get grades for a specific student and assignment
export async function getStudentAssignmentGrade(studentId: string, assignmentId: string) {
  try {
    const response = await api.get<{ grades: Grade[] }>(`/api/grades/student/${studentId}`);
    const grade = response.data.grades.find(g => g.assignmentId === assignmentId);
    return grade;
  } catch (error) {
    console.error('Error fetching grade:', error);
    return null;
  }
}

// Create or update a grade
export async function saveGrade(data: {
  studentId: string;
  assignmentId: string;
  enrollmentId: string;
  pointsEarned?: number;
  status: 'missing' | 'late' | 'submitted' | 'graded' | 'excused';
  feedback?: string;
}) {
  try {
    // Check if grade already exists
    const existingGrade = await getStudentAssignmentGrade(data.studentId, data.assignmentId);

    if (existingGrade) {
      // Update existing grade
      const response = await api.put<{ grade: Grade }>(`/api/grades/${existingGrade.id}`, {
        pointsEarned: data.pointsEarned,
        status: data.status,
        feedback: data.feedback,
      });
      return response.data.grade;
    } else {
      // Create new grade
      const response = await api.post<{ grade: Grade }>('/api/grades', data);
      return response.data.grade;
    }
  } catch (error) {
    console.error('Error saving grade:', error);
    throw error;
  }
}

// Save multiple grades at once.
//
// Nothing in the app calls this since the quick-entry sheet gave way to the
// marking surfaces, which write one mark at a time. Kept as the only client of
// `POST /api/grades/batch`, which the API still serves.
export async function saveGradesBatch(grades: {
  assignmentId: string;
  studentId: string;
  enrollmentId: string;
  pointsEarned?: number;
  status: 'missing' | 'late' | 'submitted' | 'graded' | 'excused';
}[]) {
  const response = await api.post<{ grades: Grade[] }>('/api/grades/batch', { grades });
  return response.data.grades;
}

// Delete a grade
export async function deleteGrade(gradeId: string) {
  await api.delete(`/api/grades/${gradeId}`);
}

// Create a new assignment
export async function createAssignment(data: {
  termCourseId: string;
  assignmentName: string;
  description?: string;
  totalPoints: number;
  weight: number;
  assignedDate: string;
  dueDate?: string;
  categoryId?: string;
}) {
  const response = await api.post<{ assignment: Assignment }>('/api/assignments', data);
  return response.data.assignment;
}

// Update an assignment
export async function updateAssignment(
  assignmentId: string,
  data: {
    assignmentName?: string;
    description?: string;
    totalPoints?: number;
    weight?: number;
    assignedDate?: string;
    /** `null` clears the due date; omitting the field keeps what is stored. */
    dueDate?: string | null;
    categoryId?: string;
  }
) {
  const response = await api.put<{ assignment: Assignment }>(`/api/assignments/${assignmentId}`, data);
  return response.data.assignment;
}

// Delete an assignment
export async function deleteAssignment(assignmentId: string) {
  await api.delete(`/api/assignments/${assignmentId}`);
}

// Grade Scale API functions

// Get all grade scales, optionally filtered by program type
export async function getGradeScales(programType?: string) {
  const url = programType
    ? `/api/grade-scales?programType=${programType}`
    : '/api/grade-scales';
  const response = await api.get<{ gradeScales: GradeScale[] }>(url);
  return response.data.gradeScales;
}

// Get a specific grade scale
export async function getGradeScale(id: string) {
  const response = await api.get<{ gradeScale: GradeScale }>(`/api/grade-scales/${id}`);
  return response.data.gradeScale;
}

// Create a new grade scale
export async function createGradeScale(data: Omit<GradeScale, 'id' | 'createdAt' | 'updatedAt'>) {
  const response = await api.post<{ gradeScale: GradeScale }>('/api/grade-scales', data);
  return response.data.gradeScale;
}

// Update a grade scale
export async function updateGradeScale(
  id: string,
  data: Partial<Omit<GradeScale, 'id' | 'createdAt' | 'updatedAt'>>
) {
  const response = await api.put<{ gradeScale: GradeScale }>(`/api/grade-scales/${id}`, data);
  return response.data.gradeScale;
}

// Delete a grade scale
export async function deleteGradeScale(id: string) {
  await api.delete(`/api/grade-scales/${id}`);
}

// Get default grade scales for a program type
export async function getDefaultGradeScales(programType: 'IHP' | 'DP' | 'Both' | 'Any') {
  const response = await api.get<{ gradeScales: GradeScale[] }>(
    `/api/grade-scales/default/${programType}`
  );
  return response.data.gradeScales;
}
